import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { authenticateUser } from 'src/auth';
import FlashesRenderer from 'src/components/Flashes/FlashesRenderer';
import LayoutErrorBoundary from 'src/components/Layout/LayoutErrorBoundary';
import Loading from 'src/components/Loading';
import AdminNavigation from 'src/components/Navigation/AdminNavigation';
import Footer from 'src/enosikit/components/Footer';
import { USER_TYPE_ADMIN } from 'src/util/constants';

/**
 * Viewer fragment for the admin page shell.
 * Unlike `CustomerLayout_viewer`, this layout fragment throws on field errors: every
 * field feeds the admin authorisation check, and a field error on `viewerUser` would
 * otherwise read as "not an admin" and redirect to the customer view.
 * @see docs/solutions/graphql/customer-navigation-field-error-handling.md
 */
const AdminLayoutViewerFragment = graphql`
  fragment AdminLayout_viewer on Viewer @throwOnFieldError {
    id
    viewerUser {
      uuid
      type
    }
    ...AdminNavigation_viewer
  }
`;

/**
 * Renders the authenticated admin page shell: navigation, flash messages, page content
 * and footer. Returns a loading spinner while authorisation redirects.
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - Page content to render inside the layout.
 * @param {object} props.router - Found router used by authenticateUser for redirects.
 * @param {object|null} props.viewer - Relay fragment reference for the authenticated viewer.
 * @returns {React.ReactElement} The composed layout or a loading spinner.
 */
function AdminLayoutContent({ children, router, viewer: viewerRef = null }) {
  const intl = useIntl();
  const viewer = useFragment(AdminLayoutViewerFragment, viewerRef);

  if (!authenticateUser(intl, viewer, USER_TYPE_ADMIN, router)) {
    return <Loading />;
  }

  const pageTitle = intl.formatMessage({ id: 'layout.admin_layout.page.title', defaultMessage: 'Enosi Administration' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <div className="flex flex-column" style={{ minHeight: '80vh' }}>
        <AdminNavigation viewer={viewer} />

        <section id="main">
          <FlashesRenderer />
          <div className="container">
            <div className="flex-auto">{children}</div>
          </div>
        </section>

        <Footer versionUI={VERSION} />
      </div>
    </>
  );
}

/**
 * Prop types shared by `AdminLayout` and `AdminLayoutContent`.
 * The `viewer` prop is an opaque Relay fragment reference, so it is typed loosely.
 */
const adminLayoutPropTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  router: PropTypes.shape({
    replace: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

AdminLayoutContent.propTypes = adminLayoutPropTypes;

/**
 * AdminLayout component that provides an error boundary around the admin page shell.
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - Page content to render inside the layout.
 * @param {object} props.router - Found router used for redirects.
 * @param {object|null} props.viewer - Relay fragment reference for the authenticated viewer.
 * @returns {React.ReactElement} AdminLayout component.
 */
function AdminLayout({ children, router, viewer = null }) {
  return (
    <LayoutErrorBoundary>
      <AdminLayoutContent router={router} viewer={viewer}>
        {children}
      </AdminLayoutContent>
    </LayoutErrorBoundary>
  );
}

AdminLayout.propTypes = adminLayoutPropTypes;

export default AdminLayout;
